import { useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import PageHeader, { Page } from "@/components/layout/PageHeader";
import { Notice } from "@/components/common/States";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import ConnectionsManager from "@/components/settings/ConnectionsManager";
import FormatPicker from "@/components/generator/FormatPicker";
import { VeyraMark } from "@/components/brand/VeyraLogo";
import { Users } from "lucide-react";
import { useStudio } from "@/store/StudioContext";
import { useAuth } from "@/store/AuthContext";
import { api } from "@/lib/api";
import { repository } from "@/data/repository";
import { STANDARD_FORMATS, IMAGE_RESOLUTIONS } from "@/domain/formats";
import { notifySaved } from "@/lib/notify";
import { t, LANGUAGES, setLanguage } from "@/i18n";

const STEP_KEYS = ["access", "workspace", "connections", "assistant", "budget", "first"];

const Section = ({ id, title, children }) => (
  <section id={id} data-testid={`settings-section-${id}`} className="v-panel p-5 md:p-6">
    <h2 className="mb-4 text-lg font-semibold">{title}</h2>
    <div className="flex flex-col gap-4">{children}</div>
  </section>
);

const Row = ({ label, hint, children }) => (
  <div className="grid gap-2 md:grid-cols-[220px_1fr] md:items-start">
    <div><div className="text-sm font-medium">{label}</div>{hint && <div className="mt-0.5 text-xs text-[#a3a3a3]">{hint}</div>}</div>
    <div>{children}</div>
  </div>
);

const StepStatusBadge = ({ status }) => {
  const styles = status === "completed" ? "border-[#f5b544] text-[#f5b544]" : status === "skipped" ? "border-[#6b6b6b] text-[#6b6b6b]" : "border-[#303030] text-[#a3a3a3]";
  return <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${styles}`}>{t.stepStatus[status]}</span>;
};

const SettingsPage = () => {
  const { state, dispatch } = useStudio();
  const { user, logout } = useAuth();
  const isOwner = user?.role === "owner" || user?.id === "owner" || user?._id === "owner";
  const ob = state.onboarding;
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const [changePw, setChangePw] = useState({ open: false, cur: "", nxt: "", confirm: "", error: null, busy: false });
  const setCp = (k) => (e) => setChangePw((s) => ({ ...s, [k]: e.target.value }));
  const submitChangePw = async (e) => {
    e.preventDefault();
    if (changePw.nxt.length < 12) { setChangePw((s) => ({ ...s, error: t.auth.newPasswordTooShort })); return; }
    if (changePw.nxt !== changePw.confirm) { setChangePw((s) => ({ ...s, error: t.auth.changePasswordMismatch })); return; }
    setChangePw((s) => ({ ...s, busy: true, error: null }));
    try {
      await api.changePassword({ current_password: changePw.cur, new_password: changePw.nxt });
      setChangePw({ open: false, cur: "", nxt: "", confirm: "", error: null, busy: false });
      toast.success(t.auth.changePasswordSuccess);
    } catch (err) {
      setChangePw((s) => ({ ...s, busy: false, error: t.auth[err.message] || t.auth.generic }));
    }
  };
  const localBackup = repository.load();
  const hasLocalBackup = Boolean(localBackup && !localBackup.__error && Array.isArray(localBackup.projects));
  const update = (patch) => dispatch({ type: "ONBOARDING_UPDATE", payload: patch });
  // Marca un paso del onboarding como completado cuando el propietario edita
  // su equivalente directamente en Ajustes (fuera del asistente de onboarding),
  // para que "Pendiente" no se quede mal reflejado teniendo valores configurados.
  const markStepDone = (key) => dispatch({ type: "ONBOARDING_UPDATE", payload: { stepStatus: { ...ob.stepStatus, [key]: "completed" } } });
  const runningJobs = state.jobs.filter((j) => j.status === "pending" || j.status === "running").length;
  const doLogout = async () => { setLoggingOut(true); await logout(); };

  return (
    <Page testId="settings-page">
      <PageHeader title={t.settings.title} />
      <div className="flex flex-col gap-5">
        <Section id="profile" title={t.settings.sections.profile}>
          <Row label={t.settings.ownerName}><input data-testid="settings-owner-name" className="v-input max-w-md" value={ob.ownerName} onChange={(e) => update({ ownerName: e.target.value })} onBlur={() => { markStepDone("access"); notifySaved(); }} /></Row>
          <Row label={t.settings.language} hint={t.onboarding.access.languageHint}>
            <div className="flex gap-2">
              {LANGUAGES.map((l) => <button key={l.code} data-testid={`settings-language-${l.code}`} className={`v-btn-ghost v-btn-sm ${ob.language === l.code ? "border-[#f5b544] text-[#f5b544]" : ""}`} onClick={() => { update({ language: l.code }); setLanguage(l.code); markStepDone("access"); notifySaved(); }}>{l.code === "es" ? t.onboarding.workspace.es : t.onboarding.workspace.en}</button>)}
            </div>
          </Row>
          <Row label={t.settings.sessionTitle} hint={t.settings.logoutHint}>
            <button data-testid="settings-logout" disabled={loggingOut} className="v-btn-ghost v-btn-sm" onClick={doLogout}>{loggingOut ? t.auth.loggingOut : t.auth.logout}</button>
          </Row>
          {changePw.open ? (
            <form data-testid="settings-change-password-form" onSubmit={submitChangePw} className="flex flex-col gap-3 rounded-lg border border-[#303030] bg-[#151515] p-4">
              <div className="text-sm font-semibold">{t.auth.changePasswordTitle}</div>
              <input data-testid="change-pw-current" type="password" required autoComplete="current-password" className="v-input" placeholder={t.auth.currentPassword} value={changePw.cur} onChange={setCp("cur")} />
              <input data-testid="change-pw-new" type="password" required autoComplete="new-password" className="v-input" placeholder={`${t.auth.newPassword} — ${t.auth.newPasswordHint}`} value={changePw.nxt} onChange={setCp("nxt")} />
              <input data-testid="change-pw-confirm" type="password" required autoComplete="new-password" className="v-input" placeholder={t.auth.confirmPassword} value={changePw.confirm} onChange={setCp("confirm")} />
              {changePw.error && <p data-testid="change-pw-error" className="text-sm text-[#e5655a]">{changePw.error}</p>}
              <div className="flex gap-2">
                <button data-testid="change-pw-save" type="submit" disabled={changePw.busy} className="v-btn-primary v-btn-sm">{changePw.busy ? t.auth.changePasswordSaving : t.auth.changePasswordSave}</button>
                <button type="button" className="v-btn-ghost v-btn-sm" onClick={() => setChangePw((s) => ({ ...s, open: false, cur: "", nxt: "", confirm: "", error: null }))}>{t.common.cancel}</button>
              </div>
            </form>
          ) : (
            <Row label={t.settings.passwordLabel}>
              <button data-testid="change-pw-open" className="v-btn-ghost v-btn-sm" onClick={() => setChangePw((s) => ({ ...s, open: true }))}>{t.auth.changePasswordTitle}</button>
            </Row>
          )}
        </Section>

        {!isOwner && (
          <Notice tone="info" testId="guest-environment-notice">
            Entorno de Invitado Activo: Tu espacio de trabajo, proyectos, personajes y medios están 100% aislados. Las funciones de generación con IA se encuentran en pausa hasta el Bloque 2 (claves propias y control de presupuesto independiente).
          </Notice>
        )}

        {isOwner && (
          <Section id="members" title="Miembros e Invitaciones">
            <Row label="Acceso de colaboradores" hint="Genera invitaciones de un solo uso y gestiona los accesos independientes.">
              <button
                type="button"
                data-testid="settings-manage-invitations"
                className="v-btn-primary v-btn-sm inline-flex items-center gap-2"
                onClick={() => navigate("/admin?tab=users")}
              >
                <Users size={14} /> Gestionar Miembros e Invitaciones
              </button>
            </Row>
          </Section>
        )}

        <Section id="workspace" title={t.settings.sections.workspace}>
          <Row label={t.settings.workspaceName}><input data-testid="settings-workspace-name" className="v-input max-w-md" value={ob.workspaceName} onChange={(e) => update({ workspaceName: e.target.value })} onBlur={() => { markStepDone("workspace"); notifySaved(); }} /></Row>
          <Row label={t.settings.formatPref} hint={t.onboarding.workspace.formatPrefHint}>
            <FormatPicker formats={STANDARD_FORMATS} value={ob.workspacePrefs.format} onChange={(f) => { update({ workspacePrefs: { ...ob.workspacePrefs, format: f } }); markStepDone("workspace"); notifySaved(); }} testPrefix="settings-format" />
          </Row>
          <Row label={t.settings.resolutionPref} hint={t.onboarding.workspace.resolutionPrefHint}>
            <div className="flex flex-wrap gap-1.5">
              {IMAGE_RESOLUTIONS.map((r) => <button key={r} data-testid={`settings-resolution-${r}`} className={`v-btn-ghost v-btn-sm ${ob.workspacePrefs.imageResolution === r ? "border-[#f5b544] text-[#f5b544]" : ""}`} onClick={() => { update({ workspacePrefs: { ...ob.workspacePrefs, imageResolution: r } }); markStepDone("workspace"); notifySaved(); }}>{r}</button>)}
            </div>
          </Row>
          {ob.status !== "completed" && <Notice tone="warn" testId="settings-onboarding-notice">{t.banners.onboardingIncomplete}. <button className="underline" data-testid="settings-resume-onboarding" onClick={() => navigate("/onboarding")}>{t.banners.onboardingResume}</button></Notice>}
          <div>
            <div className="mb-2 text-xs font-medium text-[#a3a3a3]">{t.settings.onboardingStepsTitle}</div>
            <div className="flex flex-wrap gap-2" data-testid="settings-step-status-list">
              {STEP_KEYS.map((k, i) => (
                <div key={k} className="flex items-center gap-1.5 rounded-lg border border-[#262626] px-2.5 py-1.5 text-xs">
                  <span className="text-[#a3a3a3]">{t.onboarding.steps[i]}</span>
                  <StepStatusBadge status={ob.stepStatus[k]} />
                </div>
              ))}
            </div>
          </div>
        </Section>

        <Section id="connections" title={t.settings.sections.connections}>
          <ConnectionsManager />
        </Section>

        <Section id="budget" title={t.settings.sections.budget}>
          <Row label={t.settings.budgetLimit} hint={t.onboarding.budget.body}>
            <input data-testid="settings-budget-limit" type="number" min="0" className="v-input max-w-[200px]" placeholder={t.onboarding.budget.noLimit} value={ob.budget.monthlyLimit ?? ""} onChange={(e) => update({ budget: { ...ob.budget, monthlyLimit: e.target.value === "" ? null : Number(e.target.value) } })} onBlur={() => { markStepDone("budget"); notifySaved(); }} />
          </Row>
          <Row label={t.settings.budgetAlert}><input data-testid="settings-budget-alert" type="number" min="1" max="100" className="v-input max-w-[200px]" value={ob.budget.alertPct} onChange={(e) => update({ budget: { ...ob.budget, alertPct: Number(e.target.value) } })} onBlur={() => { markStepDone("budget"); notifySaved(); }} /></Row>
          <Row label={t.settings.budgetBlock} hint={t.settings.budgetBlockHint}>
            <label className="flex items-center gap-2">
              <input data-testid="settings-budget-block" type="checkbox" className="accent-[#f5b544]" checked={ob.budget.blockOnLimit} onChange={(e) => { update({ budget: { ...ob.budget, blockOnLimit: e.target.checked } }); markStepDone("budget"); notifySaved(); }} />
              <span className="text-sm text-[#d8d4cd]">{t.settings.budgetBlockToggleLabel}</span>
            </label>
          </Row>
          <Row label={t.home.spent}><div className="font-mono text-sm text-[#a3a3a3]" data-testid="settings-spent">{t.home.spentNone}</div></Row>
          <div className="border-t border-[#262626] pt-3">
            <div className="mb-2 text-xs font-medium text-[#a3a3a3]">{t.settings.budgetBreakdownTitle}</div>
            <dl className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4" data-testid="settings-budget-breakdown">
              <div><dt className="text-xs text-[#a3a3a3]">{t.settings.budgetBreakdown.image}</dt><dd className="font-mono text-[#d8d4cd]">{t.tasks.costUnknown}</dd></div>
              <div><dt className="text-xs text-[#a3a3a3]">{t.settings.budgetBreakdown.video}</dt><dd className="font-mono text-[#d8d4cd]">{t.tasks.costUnknown}</dd></div>
              <div><dt className="text-xs text-[#a3a3a3]">{t.settings.budgetBreakdown.assistant}</dt><dd className="font-mono text-[#d8d4cd]">{t.tasks.costUnknown}</dd></div>
              <div><dt className="text-xs text-[#a3a3a3]">{t.settings.budgetBreakdown.audio}</dt><dd className="font-mono text-[#d8d4cd]">{t.tasks.costUnknown}</dd></div>
            </dl>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-[#262626] bg-[#151515] px-3 py-2" data-testid="settings-running-tasks">
            <span className="text-sm text-[#a3a3a3]">{t.settings.runningTasksLabel}</span>
            <span className="font-mono text-sm text-[#f5b544]">{runningJobs}</span>
          </div>
        </Section>

        <Section id="data" title={t.settings.sections.data}>
          <Row label={t.settings.storage} hint={t.settings.storageBody}><div className="text-sm text-[#a3a3a3]">{t.banners.localOnly}</div></Row>
          {hasLocalBackup && (
            <Notice tone="warn" testId="settings-import-local-notice">
              {t.settings.importLocalBody}
              <div className="mt-2"><button data-testid="settings-import-local" className="v-btn-ghost v-btn-sm" onClick={() => setConfirm("import")}>{t.settings.importLocalButton}</button></div>
            </Notice>
          )}
          <Row label={t.settings.resetDemo} hint={t.settings.resetDemoBody}><button data-testid="settings-reset-demo" className="v-btn-ghost v-btn-sm" onClick={() => setConfirm("reset")}>{t.settings.resetDemo}</button></Row>
          <Row label={t.settings.clearAll} hint={t.settings.clearAllBody}><button data-testid="settings-clear-all" className="v-btn-ghost v-btn-sm border-[#4a2a27] text-[#e5655a] hover:bg-[#1c1414]" onClick={() => setConfirm("clear")}>{t.settings.clearAll}</button></Row>
        </Section>

        <Section id="about" title={t.settings.sections.about}>
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-[#303030] bg-[#151515]"><VeyraMark size={32} /></div>
            <div className="text-sm text-[#a3a3a3]">
              <p data-testid="settings-about">{t.settings.aboutBody}</p>
              <p className="mt-2 text-xs">{t.settings.logoNote}</p>
            </div>
          </div>
        </Section>
      </div>

      <ConfirmDialog
        open={Boolean(confirm)}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={confirm === "reset" ? t.settings.resetDemo : confirm === "import" ? t.settings.importLocal : t.settings.clearAll}
        body={(() => {
          if (confirm === "import") return t.settings.confirmImport;
          if (confirm === "reset") {
            const realItems = [...state.projects, ...state.assets, ...state.jobs, ...state.scenes, ...state.characters].filter((x) => !x.isDemo).length;
            return typeof t.settings.confirmReset === "function" ? t.settings.confirmReset(realItems) : t.settings.confirmReset;
          }
          const total = state.projects.length + state.assets.length + state.jobs.length + state.scenes.length + state.characters.length;
          return typeof t.settings.confirmClear === "function" ? t.settings.confirmClear(total) : t.settings.confirmClear;
        })()}
        confirmLabel={t.common.confirm}
        onConfirm={() => {
          if (confirm === "reset") dispatch({ type: "RESET_DEMO" });
          else if (confirm === "import") { dispatch({ type: "HYDRATE", payload: localBackup }); notifySaved(); }
          else { dispatch({ type: "CLEAR_ALL" }); navigate("/onboarding"); }
        }}
      />
    </Page>
  );
};

export default SettingsPage;
